import React from 'react';

interface PieceValue {
  boardSize: string;
  value: number;
}

interface PieceValueGraphProps {
  pieceName: string;
  pieceAbbreviation: string;
  values: PieceValue[];
  pieceClass: string;
  onClose: () => void;
}

const PieceValueGraph: React.FC<PieceValueGraphProps> = ({
  pieceName,
  pieceAbbreviation,
  values,
  pieceClass,
  onClose
}) => {
  const getClassColor = (pieceClass: string): string => {
    const colors: Record<string, string> = {
      ruling: '#4169E1',
      architectural: '#808080',
      religious: '#9370DB',
      military: '#DC143C',
      weaponry: '#708090',
      special: '#20B2AA'
    };
    return colors[pieceClass] || '#4A90E2';
  };

  const maxValue = Math.max(...values.map(v => v.value));
  const yAxisMax = Math.ceil(maxValue * 1.1); // 10% padding
  const graphHeight = 300;
  const graphWidth = 600;
  const padding = { top: 20, right: 40, bottom: 60, left: 60 };
  const barWidth = (graphWidth - padding.left - padding.right) / values.length * 0.7;
  const barSpacing = (graphWidth - padding.left - padding.right) / values.length;

  const getBarHeight = (value: number) => {
    return (value / yAxisMax) * (graphHeight - padding.top - padding.bottom);
  };

  const getYPosition = (value: number) => {
    return graphHeight - padding.bottom - getBarHeight(value);
  };

  // Generate Y-axis labels
  const yAxisLabels = [];
  const steps = 5;
  for (let i = 0; i <= steps; i++) {
    const value = (yAxisMax / steps) * i;
    yAxisLabels.push(value);
  }

  const primaryColor = getClassColor(pieceClass);

  // Construct the piece image path (assuming images are in public/pieces folder)
  const pieceImagePath = `/pieces/${pieceAbbreviation.toLowerCase()}.png`;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '15px',
        padding: '30px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img 
              src={pieceImagePath} 
              alt={pieceName}
              style={{
                width: '75px',
                height: '75px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                // Fallback if image doesn't exist
                e.currentTarget.style.display = 'none';
              }}
            />
            <div>
              <h2 style={{ 
                margin: 0, 
                color: '#333',
                fontSize: '24px'
              }}>
                {pieceName} ({pieceAbbreviation}) - Value Analysis
              </h2>
              <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                Class: {pieceClass.charAt(0).toUpperCase() + pieceClass.slice(1)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px 10px',
              borderRadius: '5px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        <svg width={graphWidth} height={graphHeight} style={{ display: 'block', margin: '0 auto' }}>
          {/* Y-axis */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={graphHeight - padding.bottom}
            stroke="#333"
            strokeWidth="2"
          />

          {/* X-axis */}
          <line
            x1={padding.left}
            y1={graphHeight - padding.bottom}
            x2={graphWidth - padding.right}
            y2={graphHeight - padding.bottom}
            stroke="#333"
            strokeWidth="2"
          />

          {/* Y-axis labels and grid lines */}
          {yAxisLabels.map((label, index) => {
            const y = graphHeight - padding.bottom - (index / steps) * (graphHeight - padding.top - padding.bottom);
            return (
              <g key={index}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={graphWidth - padding.right}
                  y2={y}
                  stroke="#e0e0e0"
                  strokeWidth="1"
                  strokeDasharray="5,5"
                />
                <text
                  x={padding.left - 10}
                  y={y + 5}
                  textAnchor="end"
                  fontSize="12"
                  fill="#666"
                >
                  {label.toFixed(2)}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {values.map((item, index) => {
            const x = padding.left + index * barSpacing + (barSpacing - barWidth) / 2;
            const barHeight = getBarHeight(item.value);
            const y = getYPosition(item.value);

            return (
              <g key={index}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={primaryColor}
                  opacity="0.8"
                  rx="3"
                />
                <text
                  x={x + barWidth / 2}
                  y={y - 5}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#333"
                  fontWeight="bold"
                >
                  {item.value.toFixed(2)}
                </text>
              </g>
            );
          })}

          {/* X-axis labels */}
          {values.map((item, index) => {
            const x = padding.left + index * barSpacing + barSpacing / 2;
            return (
              <text
                key={index}
                x={x}
                y={graphHeight - padding.bottom + 20}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
              >
                {item.boardSize}
              </text>
            );
          })}

          {/* Axis labels */}
          <text
            x={graphWidth / 2}
            y={graphHeight - 10}
            textAnchor="middle"
            fontSize="14"
            fill="#333"
            fontWeight="bold"
          >
            Board Size
          </text>
          <text
            x={20}
            y={graphHeight / 2}
            textAnchor="middle"
            fontSize="14"
            fill="#333"
            fontWeight="bold"
            transform={`rotate(-90, 20, ${graphHeight / 2})`}
          >
            Piece Value
          </text>
        </svg>

        <div style={{ marginTop: '20px', textAlign: 'center', color: '#666', fontSize: '13px' }}>
          <p style={{ margin: '5px 0' }}>
            Values normalized with Pawn = 1.00
          </p>
          <p style={{ margin: '5px 0' }}>
            Min: {Math.min(...values.map(v => v.value)).toFixed(2)} | 
            Max: {Math.max(...values.map(v => v.value)).toFixed(2)} | 
            Avg: {(values.reduce((sum, v) => sum + v.value, 0) / values.length).toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PieceValueGraph;